import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';

interface StackedBarTrendProps<T> {
  data: T[];
  xKey: keyof T & string;
  /** Each stacked series: a `keyof T` data key, a legend name, and a resolved `hsl(...)` color. */
  series: { key: keyof T & string; name: string; color: string }[];
  height?: number;
  xTickFormatter?: (value: string) => string;
  /** Optional vertical reference marker, e.g. "Gate deployed". */
  reference?: { x: string; label: string };
}

/**
 * Generic stacked-column trend (86d3peur0 Phase C). Colors are semantic-by-meaning (passed in from
 * `useChartColors`), never index-based. House dataviz styling: dashed 3-3 `border` grid, 11px muted ticks,
 * card-styled tooltip, bars ≤24px with a rounded top cap.
 */
export function StackedBarTrend<T>({
  data,
  xKey,
  series,
  height = 240,
  xTickFormatter,
  reference,
}: StackedBarTrendProps<T>) {
  const c = useChartColors();
  const lastIndex = series.length - 1;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 0, right: 12, top: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.border} vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: c.muted }}
            stroke={c.border}
            tickFormatter={xTickFormatter}
          />
          <YAxis tick={{ fontSize: 11, fill: c.muted }} stroke={c.border} allowDecimals={false} width={36} />
          <Tooltip
            cursor={{ fill: c.muted, fillOpacity: 0.08 }}
            contentStyle={{
              fontSize: 12,
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 6,
              color: c.foreground,
            }}
            labelStyle={{ color: c.foreground }}
            itemStyle={{ color: c.foreground }}
            labelFormatter={xTickFormatter ? (label) => xTickFormatter(String(label)) : undefined}
          />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            formatter={(value) => <span style={{ color: c.foreground }}>{value}</span>}
          />
          {reference && (
            <ReferenceLine
              x={reference.x}
              stroke={c.muted}
              strokeDasharray="4 4"
              label={{ value: reference.label, fontSize: 10, fill: c.muted, position: 'top' }}
            />
          )}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stackId="s"
              fill={s.color}
              maxBarSize={24}
              radius={i === lastIndex ? [4, 4, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
